import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import {
  Bell,
  BookmarkCheck,
  HandCoins,
  Edit,
  Upload,
  Bot,
  FileBarChart,
  Loader2,
  RefreshCw,
  CheckCheck,
  Check,
  X,
  UserCog,
  UserCheck,
  ExternalLink,
} from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  notificationService,
  Notification,
} from "@/services/notificationService";
import { useAuth } from "@/contexts/AuthContext";

const NotificationPage = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState<string | null>(null);
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  const loadNotifications = async (showRefreshLoader = false) => {
    try {
      if (showRefreshLoader) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const notificationList = await notificationService.getNotifications();
      setNotifications(notificationList);
    } catch (error) {
      console.error("Failed to load notifications:", error);
      toast({
        title: "Error",
        description: "Failed to load notifications. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      setMarkingAsRead(notificationId);
      await notificationService.markAsRead(notificationId);

      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, status: "READ" as const, readAt: new Date().toISOString() }
            : notification
        )
      );

      toast({
        title: "Success",
        description: "Notification marked as read.",
      });
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      toast({
        title: "Error",
        description: "Failed to mark notification as read. Please try again.",
        variant: "destructive",
      });
    } finally {
      setMarkingAsRead(null);
    }
  };

  const handleMarkAllAsRead = async () => {
    const unreadNotifications = notifications.filter(n => n.status === "UNREAD");

    if (unreadNotifications.length === 0) return;

    try {
      setMarkingAsRead("all");

      const promises = unreadNotifications.map(notification =>
        notificationService.markAsRead(notification.id)
      );

      await Promise.all(promises);

      setNotifications(prev =>
        prev.map(notification => ({
          ...notification,
          status: "READ" as const,
          readAt: notification.status === "UNREAD" ? new Date().toISOString() : notification.readAt
        }))
      );

      toast({
        title: "Success",
        description: "All notifications marked as read.",
      });
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
      toast({
        title: "Error",
        description: "Failed to mark all notifications as read. Please try again.",
        variant: "destructive",
      });
    } finally {
      setMarkingAsRead(null);
    }
  };

  const handleApproveRoleChange = async (requestId: string, notificationId: string) => {
    try {
      setProcessingRequest(requestId);
      await notificationService.approveRoleChangeRequest(requestId);

      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? {
              ...notification,
              type: "ROLE_CHANGE_APPROVED",
              title: "Role Change Request Approved",
              message: notification.message.replace("- Pending", "- Approved"),
              metadata: { ...notification.metadata, status: "APPROVED" },
            }
            : notification
        )
      );

      toast({
        title: "Success",
        description: "Role change request approved successfully.",
      });

      await loadNotifications();
    } catch (error) {
      console.error("Failed to approve role change request:", error);
      toast({
        title: "Error",
        description: "Failed to approve role change request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleRejectRoleChange = async (requestId: string, notificationId: string) => {
    try {
      setProcessingRequest(requestId);
      await notificationService.rejectRoleChangeRequest(requestId);

      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? {
              ...notification,
              type: "ROLE_CHANGE_REJECTED",
              title: "Role Change Request Rejected",
              message: notification.message.replace("- Pending", "- Rejected"),
              metadata: { ...notification.metadata, status: "REJECTED" },
            }
            : notification
        )
      );

      toast({
        title: "Success",
        description: "Role change request rejected successfully.",
      });

      await loadNotifications();
    } catch (error) {
      console.error("Failed to reject role change request:", error);
      toast({
        title: "Error",
        description: "Failed to reject role change request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessingRequest(null);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "DEAL_CREATED":
        return HandCoins;
      case "DEAL_UPDATED":
        return Edit;
      case "DOCUMENT_UPLOADED":
        return Upload;
      case "AI_REVIEW_COMPLETED":
        return Bot;
      case "REPORT_GENERATED":
        return FileBarChart;
      case "ROLE_CHANGE_REQUESTED":
        return UserCog;
      case "ROLE_CHANGE_APPROVED":
      case "ROLE_CHANGE_REJECTED":
        return UserCheck;
      default:
        return Bell;
    }
  };

  const getNotificationLink = (notification: Notification) => {
    switch (notification.type) {
      case "DEAL_CREATED":
      case "DEAL_UPDATED":
        return `/deals/${notification.dealId}`;
      case "DOCUMENT_UPLOADED":
        return `/document-upload?dealId=${notification.dealId}`;
      case "AI_REVIEW_COMPLETED":
        return `/ai-review?dealId=${notification.dealId}`;
      case "REPORT_GENERATED":
        return `/report-generation?dealId=${notification.dealId}`;
      default:
        return `/deals/${notification.dealId || ""}`;
    }
  };

  const getRoleChangeStatus = (notification: Notification) => {
    if (notification.type === "ROLE_CHANGE_APPROVED") {
      return "Approved";
    } else if (notification.type === "ROLE_CHANGE_REJECTED") {
      return "Rejected";
    } else if (notification.type === "ROLE_CHANGE_REQUESTED") {
      return notification.metadata?.status || "Pending";
    }
    return notification.metadata?.status || "Pending";
  };

  const groupedNotifications = notificationService.groupNotificationsByDate(notifications);
  const unreadCount = notifications.filter(n => n.status === "UNREAD").length;
  const isAdmin = user?.role === "ADMIN";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-background to-gray-100/50 dark:from-gray-900 dark:via-background dark:to-gray-800/50 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-light text-foreground">Notifications</h1>
            <p className="text-muted-foreground font-light">
              Stay updated with your deal activities and role change requests
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadNotifications(true)}
              disabled={refreshing}
              className="flex items-center gap-2"
            >
              <RefreshCw
                className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
              />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            {unreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkAllAsRead}
                disabled={markingAsRead === "all"}
                className="flex items-center gap-2"
              >
                {markingAsRead === "all" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCheck className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">Mark All Read</span>
              </Button>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin" />
            <span className="ml-2">Loading notifications...</span>
          </div>
        )}

        {!loading && notifications.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Bell className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No notifications</h3>
              <p className="text-muted-foreground text-center font-light">
                You're all caught up! New notifications will appear here when there's activity.
              </p>
            </CardContent>
          </Card>
        )}

        {!loading && notifications.length > 0 && (
          <div className="space-y-6">
            {Object.entries(groupedNotifications).map(([dateGroup, dateNotifications]) => (
              <div key={dateGroup} className="space-y-3">
                <h3 className="text-sm font-medium text-muted-foreground px-1">
                  {dateGroup}
                </h3>
                <div className="space-y-2">
                  {dateNotifications.map((notification) => {
                    const IconComponent = getNotificationIcon(notification.type);
                    const isUnread = notification.status === "UNREAD";
                    const isRoleChangeRequest = notification.type === "ROLE_CHANGE_REQUESTED";
                    const isPending = notification.metadata?.status === "PENDING";
                    const isRoleChangeRelated = [
                      "ROLE_CHANGE_REQUESTED",
                      "ROLE_CHANGE_APPROVED",
                      "ROLE_CHANGE_REJECTED"
                    ].includes(notification.type);

                    return (
                      <Card
                        key={notification.id}
                        className={cn(
                          "hover:shadow-sm transition-all duration-200",
                          isUnread && "border-l-4 border-l-primary bg-accent/20"
                        )}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div
                              className={cn(
                                "flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0",
                                notificationService.getNotificationTypeColor(notification.type)
                              )}
                            >
                              <IconComponent className="w-4 h-4" />
                            </div>

                            <div className="flex-1 min-w-0 space-y-2">
                              <div className="flex items-start justify-between gap-2">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2">
                                    <h4 className="text-sm font-medium">
                                      {notification.title}
                                    </h4>
                                    {isUnread && (
                                      <Badge variant="secondary" className="text-xs px-1.5 py-0.5">
                                        New
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground font-light">
                                    {notification.message}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {notificationService.getTimeAgo(notification.createdAt)}
                                  </p>
                                </div>

                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {isUnread && (
                                    <div className="relative group">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleMarkAsRead(notification.id)}
                                        disabled={markingAsRead === notification.id}
                                        className="h-8 w-8 p-0 hover:bg-gray-100 transition-colors duration-200"
                                      >
                                        {markingAsRead === notification.id ? (
                                          <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : (
                                          <BookmarkCheck className="w-3 h-3 text-gray-600 hover:text-gray-800" />
                                        )}
                                      </Button>
                                      <span className="absolute hidden group-hover:block text-xs bg-gray-800 text-white p-1 rounded -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                                        Mark as read
                                      </span>
                                    </div>
                                  )}
                                  {!isRoleChangeRelated && (
                                    <div className="relative group">
                                      <Link to={getNotificationLink(notification)}>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0 hover:bg-gray-100 transition-colors duration-200"
                                        >
                                          <ExternalLink className="w-3 h-3 text-gray-600 hover:text-gray-800" />
                                        </Button>
                                      </Link>
                                      <span className="absolute hidden group-hover:block text-xs bg-gray-800 text-white p-1 rounded -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                                        View details
                                      </span>
                                    </div>
                                  )}
                                  {isAdmin && isRoleChangeRequest && isPending && (
                                    <>
                                      <div className="relative group">
                                        <div
                                          onClick={() =>
                                            handleApproveRoleChange(
                                              notification.metadata?.roleChangeRequestId || "",
                                              notification.id
                                            )
                                          }
                                          className={cn(
                                            "flex items-center justify-center h-8 w-8 rounded-full bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-700 cursor-pointer transition-colors duration-200",
                                            processingRequest === notification.metadata?.roleChangeRequestId ||
                                              !notification.metadata?.roleChangeRequestId
                                              ? "opacity-50 cursor-not-allowed"
                                              : ""
                                          )}
                                        >
                                          {processingRequest === notification.metadata?.roleChangeRequestId ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                          ) : (
                                            <Check className="w-3 h-3" />
                                          )}
                                        </div>
                                        <span className="absolute hidden group-hover:block text-xs bg-gray-800 text-white p-1 rounded -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                                          Accept
                                        </span>
                                      </div>
                                      <div className="relative group">
                                        <div
                                          onClick={() =>
                                            handleRejectRoleChange(
                                              notification.metadata?.roleChangeRequestId || "",
                                              notification.id
                                            )
                                          }
                                          className={cn(
                                            "flex items-center justify-center h-8 w-8 rounded-full bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-700 cursor-pointer transition-colors duration-200",
                                            processingRequest === notification.metadata?.roleChangeRequestId ||
                                              !notification.metadata?.roleChangeRequestId
                                              ? "opacity-50 cursor-not-allowed"
                                              : ""
                                          )}
                                        >
                                          {processingRequest === notification.metadata?.roleChangeRequestId ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                          ) : (
                                            <X className="w-3 h-3" />
                                          )}
                                        </div>
                                        <span className="absolute hidden group-hover:block text-xs bg-gray-800 text-white p-1 rounded -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                                          Reject
                                        </span>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>

                              {notification.metadata && (
                                <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2 mt-2">
                                  {notification.type === "DOCUMENT_UPLOADED" && (
                                    <div className="flex items-center gap-2">
                                      <Upload className="w-3 h-3" />
                                      <span>
                                        {notification.metadata.documentName} ({notification.metadata.documentType})
                                      </span>
                                    </div>
                                  )}
                                  {notification.type === "AI_REVIEW_COMPLETED" && (
                                    <div className="flex items-center gap-2">
                                      <Bot className="w-3 h-3" />
                                      <span>AI Review: {notification.metadata.reviewStatus}</span>
                                    </div>


                                  )}
                                  {notification.type === "REPORT_GENERATED" && (
                                    <div className="flex items-center gap-2">
                                      <FileBarChart className="w-3 h-3" />
                                      <span>Report: {notification.metadata.reportName}</span>
                                    </div>
                                  )}
                                  {(notification.type === "DEAL_CREATED" || notification.type === "DEAL_UPDATED") && (
                                    <div className="flex items-center gap-2">
                                      <HandCoins className="w-3 h-3" />
                                      <span>Deal: {notification.metadata.dealName}</span>
                                    </div>
                                  )}
                                  {(notification.type === "ROLE_CHANGE_REQUESTED" ||
                                    notification.type === "ROLE_CHANGE_APPROVED" ||
                                    notification.type === "ROLE_CHANGE_REJECTED") && (
                                      <div className="flex items-center gap-2">
                                        <UserCog className="w-3 h-3" />
                                        <span>
                                          Role Change Request: {getRoleChangeStatus(notification)}
                                        </span>
                                      </div>
                                    )}
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationPage;